import { slugify, cx } from '@/utils/strings'

import Quote from '@/components/ui/quote'
import Image from '@/components/ui/image'
import Slider from '@/components/ui/slider/slider'
import type {
  TypeComponentQuote,
  TypeComponentTable,
  TypeResourceImage,
  TypeSectionSetOfCards,
  TypeSectionSlider,
} from '@/types/ctf'

import PresentationSlide from '../ui/slider/presentation-slide'
import Table from '../table'
import { SetOfCardsSectionComponent } from './set-of-cards'
import { useInspectorProps } from '@/utils/live-preview'

type SectionSlider = TypeSectionSlider<'WITHOUT_UNRESOLVABLE_LINKS'>
type QuoteEntry = TypeComponentQuote<'WITHOUT_UNRESOLVABLE_LINKS'>
type ImageEntry = TypeResourceImage<'WITHOUT_UNRESOLVABLE_LINKS'>
type SetOfCards = TypeSectionSetOfCards<'WITHOUT_UNRESOLVABLE_LINKS'>
type TableEntry = TypeComponentTable<'WITHOUT_UNRESOLVABLE_LINKS'>

export type SliderSectionProps = SectionSlider['fields'] & {
  entryId?: string
  className?: string
  pdfPath?: string
}

export default function SliderSection({
  entryId,
  className,
  pdfPath,
  headline,
  slides,
  sliderStyle,
  noBgNoPadding,
  hideLeftRightNav = true,
}: SliderSectionProps) {
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })

  return (
    <section
      id={headline ? slugify(headline) : entryId}
      className={className}
      {...pageInspectorProps({ fieldId: 'headline' })}
    >
      {headline && <h2 className="text-center mb-2 lg:mb-8 h3">{headline} </h2>}
      <Slider
        customCss="md:[--bw-slider-nav-button-distance:2.44rem]"
        noBgNoPadding={noBgNoPadding}
        sliderStyle={sliderStyle}
        pdfPath={pdfPath}
        hideLeftRightNav={hideLeftRightNav}
      >
        {slides?.map((slide, index) => {
          const id = slide?.sys.id || `slide-${index}`

          if (!slide) return null
          const { fields } = slide
          if (slide.sys.contentType.sys.id === 'componentQuote') {
            const quote = fields as QuoteEntry['fields']
            return (
              <div key={id} className={cx('mb-14', !noBgNoPadding && 'mx-4 md:mx-14 ')}>
                <Quote {...quote} layout="horizontal" />
              </div>
            )
          }
          if (slide.sys.contentType.sys.id === 'resourceImage') {
            const image = fields as ImageEntry['fields']
            return (
              <div
                key={id}
                className={cx(
                  'mx-14 mb-14 max-w-full h-full flex items-center justify-center',
                  !image.zoomable && 'pointer-events-none'
                )}
              >
                <Image {...image} />
              </div>
            )
          }

          if (slide.sys.contentType.sys.id === 'sectionSetOfCards') {
            const sectionCards = fields as SetOfCards['fields']

            return sliderStyle === 'Presentation' ? (
              <div key={id}>
                <PresentationSlide {...sectionCards} />
              </div>
            ) : (
              <SetOfCardsSectionComponent {...sectionCards} key={id} />
            )
          }

          if (slide.sys.contentType.sys.id === 'componentTable') {
            const tableData = fields as TableEntry['fields']

            return sliderStyle === 'Presentation' ? (
              <div key={id}>
                <PresentationSlide table={tableData} />
              </div>
            ) : (
              <div key={id}>
                <Table {...tableData} />
              </div>
            )
          }

          return null
        })}
      </Slider>
    </section>
  )
}
